import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { FiTarget, FiUsers, FiTrendingUp, FiAward, FiStar, FiChevronRight, FiActivity, FiTrash2, FiShare2 } from 'react-icons/fi';
import { Footer } from '../components/Footer';
import { InviteSystem } from '../components/InviteSystem';
import supabase from '../supabase/supabase';

export const Landing = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [quizScores, setQuizScores] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const myQuizCode = localStorage.getItem('dykm_my_quiz_code');
  const creatorName = localStorage.getItem('dykm_creator_name');
  const shareUrl = myQuizCode ? `${window.location.origin}/#/challenge/${myQuizCode}` : '';

  const logoUrl = "https://media-manager-c.questera.ai/greta-media/5aef0a8df517b865df3c53c45a2fd1ff726cf5f808670b49edd6820a48780cae68c41714c5c531fe697e77aede0c0572/images/aW1hZ2UvcG5n/b4fb7c8e8b2fa91d7665d00ad27aec75.png";

  const fetchScores = async () => {
    if (!myQuizCode) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('dykm_scores_1774355620612')
        .select('*')
        .eq('share_code', myQuizCode)
        .order('score', { ascending: false })
        .limit(5);

      if (!error && data) {
        setQuizScores(data);
      }
    } catch (err) {
      console.error("Fetch failed:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchScores();
    if (myQuizCode) {
      const channel = supabase
        .channel(`landing-${myQuizCode}`)
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'dykm_scores_1774355620612', filter: `share_code=eq.${myQuizCode}` },
          () => fetchScores()
        )
        .subscribe();

      return () => supabase.removeChannel(channel);
    }
  }, [myQuizCode]);

  const handleStart = (e) => {
    e.preventDefault();
    if (name.trim()) {
      navigate('/create', { state: { creatorName: name.trim() } });
    }
  };

  const handleClear = () => {
    if (window.confirm("This will remove your local quiz data. Your scores online will remain, but you won't be able to manage them from this device.")) {
      localStorage.clear();
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden bg-[#012b58]">
      {/* Background Orbs */}
      <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-[#0094f7]/20 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-[#d2ff02]/10 rounded-full blur-[100px] pointer-events-none" />

      <div className="flex-1 flex flex-col items-center justify-start py-12 px-6 z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="w-full max-w-md text-center space-y-8"
        >
          {/* Header */}
          <div className="space-y-6">
            <div className="flex justify-center">
              <motion.img 
                initial={{ scale: 0.8 }} 
                animate={{ scale: 1 }} 
                transition={{ type: "spring", stiffness: 200 }}
                src={logoUrl} 
                alt="Logo" 
                className="h-24 w-auto object-contain drop-shadow-[0_0_15px_rgba(210,255,2,0.3)]" 
              />
            </div>
            <div>
              <h1 className="text-5xl font-extrabold tracking-tight mb-4 text-white leading-tight">
                Do You <br/> <span className="text-gradient">Know Me?</span>
              </h1>
              <p className="text-slate-300 text-lg px-4">
                Challenge your friends and see who actually pays attention.
              </p>
            </div>
          </div>

          {/* Main Action Area */}
          <div className="space-y-6">
            <form onSubmit={handleStart} className="glass-panel p-6 rounded-3xl relative">
              <div className="text-left mb-4">
                <label className="block text-sm font-medium text-slate-300 mb-2 ml-1">
                  {myQuizCode ? "Create a New Quiz" : "Your Name"}
                </label>
                <input 
                  type="text" 
                  value={name} 
                  onChange={(e) => setName(e.target.value)} 
                  placeholder={creatorName ? `Welcome back, ${creatorName}` : "e.g. Alex"}
                  maxLength={15}
                  className="w-full bg-[#012b58]/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#0094f7] transition-all"
                  required 
                />
              </div>
              <Button type="submit" className="w-full text-lg h-14" disabled={!name.trim()}>
                {myQuizCode ? "Start Fresh Quiz" : "Start My Quiz"}
                <SafeIcon icon={FiTrendingUp} />
              </Button>
              
              {myQuizCode && (
                <div className="mt-4 flex flex-col gap-2">
                  <button 
                    type="button" 
                    onClick={() => navigate('/dashboard')}
                    className="text-sm text-[#0094f7] font-semibold flex items-center justify-center gap-1 hover:text-[#d2ff02] transition-colors"
                  >
                    View Result Dashboard <SafeIcon icon={FiChevronRight} />
                  </button>
                  <button 
                    type="button" 
                    onClick={handleClear}
                    className="text-[10px] text-slate-500 font-bold uppercase tracking-widest hover:text-red-400 flex items-center justify-center gap-1"
                  >
                    <SafeIcon icon={FiTrash2} /> Reset Current Quiz
                  </button>
                </div>
              )}
            </form>

            {/* Quick Invite Section for returning users */}
            {myQuizCode && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <InviteSystem shareUrl={shareUrl} creatorName={creatorName} />
              </motion.div>
            )}

            {/* Mini Leaderboard */}
            {myQuizCode && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }} 
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                className="glass-panel rounded-3xl p-6 text-left border border-white/5 shadow-2xl overflow-hidden"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-[#d2ff02]/10 rounded-lg">
                      <SafeIcon icon={FiAward} className="text-[#d2ff02] text-xl" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg leading-none">Top Scores</h3>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Recent Activity</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 bg-white/5 px-2 py-1 rounded-full">
                    <span className="w-1.5 h-1.5 bg-[#d2ff02] rounded-full animate-pulse" />
                    <span className="text-[9px] text-slate-400 font-black uppercase">Live</span>
                  </div>
                </div>

                <div className="space-y-3">
                  {isLoading && quizScores.length === 0 ? (
                    <div className="py-8 flex flex-col items-center gap-3">
                      <div className="w-6 h-6 border-2 border-[#0094f7] border-t-transparent rounded-full animate-spin" />
                      <span className="text-slate-500 text-xs font-medium">Syncing...</span>
                    </div>
                  ) : quizScores.length > 0 ? (
                    quizScores.map((score, idx) => (
                      <motion.div 
                        key={score.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/5"
                      >
                        <div className="flex items-center gap-3">
                          <span className={`text-[10px] font-black w-6 h-6 rounded-lg flex items-center justify-center ${idx === 0 ? 'bg-gradient-to-br from-[#d2ff02] to-[#329503] text-[#012b58]' : 'bg-slate-800 text-slate-400'}`}>
                            {idx + 1}
                          </span>
                          <span className="text-sm font-semibold text-slate-200">{score.challenger_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-black ${score.score >= 5 ? 'text-[#d2ff02]' : 'text-slate-400'}`}>
                            {score.score}/10
                          </span>
                          {score.score >= 8 && <SafeIcon icon={FiStar} className="text-xs text-[#d2ff02]" />}
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="py-10 text-center border-2 border-dashed border-white/5 rounded-2xl">
                      <SafeIcon icon={FiActivity} className="text-3xl text-slate-700 mx-auto mb-3" />
                      <p className="text-slate-400 text-sm font-medium">No one has played yet</p>
                      <p className="text-[10px] text-slate-600 mt-1 uppercase font-bold tracking-tighter">Share your link above!</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};